/**
 * Canonical list of Lomé quartiers (neighborhoods), grouped by arrondissement.
 *
 * Source: French Wikipedia article on Lomé (https://fr.wikipedia.org/wiki/Lomé),
 * cross-referenced with the official Togolese commune divisions. Names preserve
 * canonical French spelling with accents.
 *
 * Used by the marketing waitlist forms (Hero / Closing / Worker) as the
 * suggestion list for the quartier `<datalist>`. Free-text entries are still
 * accepted — the list is a UX hint, not a hard constraint.
 *
 * If a visitor's quartier isn't here, they can type it freely; we capture
 * whatever they enter. Update this file when Lomé's commune boundaries change
 * or when a missing quartier is reported.
 */

export interface Quartier {
  readonly name: string;
  readonly arrondissement: 1 | 2 | 3 | 4 | 5 | null;
}

export const LOME_QUARTIERS: readonly Quartier[] = [
  // 1er arrondissement (central north, administrative)
  { name: 'Abobokomé', arrondissement: 1 },
  { name: 'Adawlato', arrondissement: 1 },
  { name: 'Adoboukomé', arrondissement: 1 },
  { name: 'Agbadahonou', arrondissement: 1 },
  { name: 'Aguia Komé', arrondissement: 1 },
  { name: 'Béniglato', arrondissement: 1 },
  { name: 'Fréau Jardin', arrondissement: 1 },
  { name: 'Kokétimé', arrondissement: 1 },
  { name: 'Quartier Administratif', arrondissement: 1 },
  { name: 'Sanguéra', arrondissement: 1 },
  { name: 'Wétrivi Kondji', arrondissement: 1 },

  // 2e arrondissement (north-east, includes Tokoin sub-quartiers)
  { name: 'Adakpamé', arrondissement: 2 },
  { name: 'Akodesséwa-Kponou', arrondissement: 2 },
  { name: 'Akodesséwa-Kpota', arrondissement: 2 },
  { name: 'Anfamé', arrondissement: 2 },
  { name: 'Atiégou', arrondissement: 2 },
  { name: 'Avénou', arrondissement: 2 },
  { name: 'Bè-Kpota', arrondissement: 2 },
  { name: 'Hédzranawoé', arrondissement: 2 },
  { name: 'Kagnikopé', arrondissement: 2 },
  { name: 'Kélégougan', arrondissement: 2 },
  { name: 'Lomé 2', arrondissement: 2 },
  { name: "N'tifafakomé", arrondissement: 2 },
  { name: 'Résidence du Bénin', arrondissement: 2 },
  { name: 'Saint-Joseph', arrondissement: 2 },
  { name: 'Tokoin-Aéroport', arrondissement: 2 },
  { name: 'Tokoin-Forever', arrondissement: 2 },
  { name: "Tokoin-N'kafu", arrondissement: 2 },
  { name: 'Tokoin-Tamé', arrondissement: 2 },
  { name: 'Tokoin-Wuiti', arrondissement: 2 },

  // 3e arrondissement (east, port + Bè cluster)
  { name: 'Ablogamé', arrondissement: 3 },
  { name: 'Akodésséwa', arrondissement: 3 },
  { name: 'Amoutivé', arrondissement: 3 },
  { name: 'Anthonio Nétimé', arrondissement: 3 },
  { name: 'Bassadji', arrondissement: 3 },
  { name: 'Bè', arrondissement: 3 },
  { name: 'Bè-Ahligo', arrondissement: 3 },
  { name: 'Bè-Apéhémé', arrondissement: 3 },
  { name: 'Bè-Hédjé', arrondissement: 3 },
  { name: 'Doulassamé', arrondissement: 3 },
  { name: 'Gbényédi', arrondissement: 3 },
  { name: 'Kotokou Kondji', arrondissement: 3 },
  { name: 'Kpéhénou', arrondissement: 3 },
  { name: 'Lom Nava', arrondissement: 3 },
  { name: 'Souza Nétimé', arrondissement: 3 },
  { name: 'Wété', arrondissement: 3 },
  { name: 'Zone Portuaire', arrondissement: 3 },

  // 4e arrondissement (central coastal — historic colonial quarter)
  { name: 'Hanoukopé', arrondissement: 4 },
  { name: 'Kodjoviakopé', arrondissement: 4 },
  { name: 'Nyékonakpoé', arrondissement: 4 },
  { name: 'Octaviano Nétimé', arrondissement: 4 },

  // 5e arrondissement (north-west, Tokoin proper + university)
  { name: 'Abové', arrondissement: 5 },
  { name: 'Aflao Gakli', arrondissement: 5 },
  { name: 'Agbalépédogan', arrondissement: 5 },
  { name: 'Akossombo', arrondissement: 5 },
  { name: 'Bè-Klikamé', arrondissement: 5 },
  { name: 'Casablanca', arrondissement: 5 },
  { name: 'Dogbéavou', arrondissement: 5 },
  { name: 'Doumasséssé', arrondissement: 5 },
  { name: 'Gbonvié', arrondissement: 5 },
  { name: 'Soviépé', arrondissement: 5 },
  { name: 'Tokoin-Élavagnon', arrondissement: 5 },
  { name: 'Tokoin-Gbadago', arrondissement: 5 },
  { name: 'Tokoin-Hôpital', arrondissement: 5 },
  { name: 'Tokoin-Lycée', arrondissement: 5 },
  { name: 'Tokoin-Ouest', arrondissement: 5 },
  { name: 'Tokoin-Solidarité', arrondissement: 5 },
  { name: 'Totsi', arrondissement: 5 },
  { name: 'Université de Lomé', arrondissement: 5 },

  // Quartiers not officially assigned in the Wikipedia source but commonly cited
  { name: 'Adidogomé', arrondissement: null },
  { name: 'Adewi', arrondissement: null },
  { name: 'Adjangbakomé', arrondissement: null },
  { name: 'Agoè', arrondissement: null },
  { name: 'Attikoumè', arrondissement: null },
  { name: 'Avédji', arrondissement: null },
  { name: 'Baguida', arrondissement: null },
  { name: 'Dékon', arrondissement: null },
  { name: 'Djidjolé', arrondissement: null },
  { name: 'Kélékougan', arrondissement: null },
  { name: 'Kpogan', arrondissement: null },
  { name: 'Tokoin', arrondissement: null },
  { name: 'Xédranawoe', arrondissement: null },
];

/**
 * Sorted alphabetically — best for an autocomplete `<datalist>` where the
 * browser does substring matching anyway.
 */
export const LOME_QUARTIERS_SORTED: readonly string[] = [...LOME_QUARTIERS]
  .map((q) => q.name)
  .sort((a, b) => a.localeCompare(b, 'fr'));
